import { useNavigate } from 'react-router-dom';
import { DataGrid } from '@/components/data-grid';
import { ROUTES } from '@/app/routes';
import {
  useCoopModerationCases,
  useCoopModerationMetrics,
  type ProactiveCase,
} from '@/features/moderation/api';
import { proactiveColumns } from '@/features/moderation/columns';

/**
 * Content Management → Frontier Model Review.
 * Lists Coop cases in frontier / GPU deep analysis (status=deep → PENDING + risk_tier).
 * Row opens content detail for monitoring / follow-up.
 */
export function FrontierReviewPage() {
  const navigate = useNavigate();
  const table = useCoopModerationCases('deep');
  const metrics = useCoopModerationMetrics();
  const inQueue =
    metrics.data?.frontierQueue ?? metrics.data?.gpuEscalationsToday ?? table.total ?? 0;

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Frontier Model Review</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Posts currently in frontier / GPU deep analysis before a final pipeline decision.
          </p>
        </div>
        <span className="rounded-md border bg-muted/40 px-3 py-1.5 text-sm text-muted-foreground">
          {inQueue} in queue
        </span>
      </div>

      <div className="min-h-0 flex-1">
        <DataGrid<ProactiveCase>
          title="Frontier Model Queue"
          searchPlaceholder="Search frontier review queue…"
          columns={proactiveColumns}
          rows={table.rows}
          total={table.total}
          isLoading={table.isLoading}
          isFetchingNextPage={table.isFetchingNextPage}
          isError={table.isError}
          error={table.error}
          endReached={table.endReached}
          page={table.page}
          totalPage={table.totalPage}
          onPageChange={table.setPage}
          isFetching={table.isFetching}
          onLoadMore={table.fetchNextPage}
          onRetry={() => void table.refetch()}
          search={table.search}
          onSearchChange={table.setSearch}
          onRowClick={(row) => {
            if (row.post_id != null) {
              void navigate(ROUTES.contentDetails(String(row.post_id)));
            }
          }}
          getRowId={(row) => row.case_id}
          emptyMessage="No posts in frontier model review."
        />
      </div>
    </div>
  );
}
